import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import getTenantId from "../../components/Tenent";
import { showAccessDeniedModal, showSessionExpiredModal } from "../slices/authSlice";
const getStore = () => require("../store").store;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_ACTIVITIES_HOST_URL,
  withCredentials: true,
  headers: {
    accept: `application/json`,
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers["x-tenant-id"] = getTenantId();
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname !== "/login") {
      getStore().dispatch(showSessionExpiredModal(window.location.pathname));
    }
    return Promise.reject(err);
  },
);

const axiosBaseQuery =
  () =>
  async ({ url, method, body, params, ...requestOpts }) => {
    try {
      const isBlobRequest =
        url.includes("/gri/innovo_gri_report_excel") ||
        url.includes("/innovo_ISO_report") ||
         url.includes("/blob-from-s3") ;
      const axiosOptions = {
        url: url,
        method,
        data: body,
        params,
        headers: requestOpts.headers,
        responseType: isBlobRequest ? "blob" : "json",
      };

      const result = await axiosInstance(axiosOptions);
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError;
      const errorData = axiosError.response?.data;
      const status = axiosError.response?.status;

      if (status === 403 && errorData?.error === "forbidden") {
        getStore().dispatch(showAccessDeniedModal());
      }
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const activitiesApi = createApi({
  reducerPath: "activitiesApi",
  baseQuery: axiosBaseQuery(),
  // baseQuery: fetchBaseQuery({
  //   baseUrl: process.env.REACT_APP_ACTIVITIES_HOST_URL,
  //   prepareHeaders: async (headers, { getState }) => {
  //     const token = await getState()?.auth?.activityAccessToken;
  //     if (token) {
  //       headers.set("Authorization", `Bearer ${token}`);
  //     }
  //     return headers;
  //   },
  // }),
  tagTypes: ["activities", "CategoryConfig"],
  endpoints: (builder) => ({
    getToken: builder.mutation({
      query: () => ({
        url: `login`,
        method: "POST",
        body: {
          API_KEY: process.env.REACT_APP_ACTIVITIES_HOST_API_KEY,
        },
      }),
      invalidatesTags: ["activities"],
    }),
    getActivities: builder.query({
      query: ({ category, mapping_category, mapping_sector, token }) => {
        if (!category) {
          return;
        }
        return {
          url: `get_emission_units_factors?category=${category}&mapping_category=${mapping_category}&mapping_sector=${mapping_sector}&region=IN`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
      providesTags: ["activities"],
    }),
    getMapActivities: builder.query({
      query: ({ mapping_category, mapping_sector, token }) => {
        return {
          url: `get_activities?mapping_category=${mapping_category}&mapping_sector=${mapping_sector}&is_currency_match=1`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
      providesTags: ["activities"],
    }),
    manualMatch: builder.mutation({
      query: ({ token, body }) => {
        return {
          url: `/save_manual_match`,
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: body,
        };
      },
      providesTags: ["activities"],
    }),
    getCategoryActivities: builder.query({
      query: ({ token, category, region, search }) => {
        if (!category) {
          return;
        }
        return {
          url: `get_activities`,
          // url: `get_activities?category=${category}&search=${search}&region=${region}&add_pagination=1&n_records=80`,
          params: {
            category,
            search,
            ...(!region ? {} : { region }),
            add_pagination: 1,
            n_records: 100,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
      providesTags: ["activities"],
    }),
    searchAllActivities: builder.query({
      query: ({ q, limit, page, token }) => {
        if (!q) {
          return;
        }
        return {
          url: `show_activities?search=${q || ""}&page=${page || 1}&n_records=${
            limit || 50
          }`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
      providesTags: ["activities"],
    }),
    getInnovoScope3Waste: builder.query({
      query: ({ taxonomy, start_date, end_date, limit = 50 }) => {
        const queryParams = [
          `taxonomy=${taxonomy}`,
          `category=${12}`,
          `start_date=${start_date}`,
          `end_date=${end_date}`,
          `limit=${limit}`,
        ];
        return {
          url: `/innovo_scope3_waste?${queryParams.join("&")}`,
        };
      },
      providesTags: ["analytics"],
    }),
    getInnovoGRIReportExcel: builder.query({
      query: ({ organisation_id, year, start_date, end_date }) => ({
        url: `/gri/innovo_gri_report_excel`,
        method: "GET",
        params: {
          organisation_id,
          year,
          start_date,
          end_date,
        },
        responseHandler: (response) => response.blob(),
      }),
      providesTags: ["activities"],
    }),
    getInnovoISOReport: builder.query({
      query: ({ taxonomy_ids, start_date, end_date }) => ({
        url: `/innovo_ISO_report`,
        method: "GET",
        params: {
          taxonomy_ids,
          start_date,
          end_date,
        },
        responseHandler: (response) => response.blob(),
      }),
      providesTags: ["activities"],
    }),
    getInnovoScope1Fugitive: builder.query({
      query: ({ taxonomy, start_date, end_date, limit = 50 }) => {
        const queryParams = [
          `taxonomy=${taxonomy}`,
          `category=${3}`,
          `start_date=${start_date}`,
          `end_date=${end_date}`,
        ];
        return {
          url: `/innovo_scope1_fugitive?${queryParams.join("&")}`,
        };
      },
      providesTags: ["analytics"],
    }),
    getInnovoScope2Energy: builder.query({
      query: ({ taxonomy, start_date, end_date, category = 5 }) => {
        const queryParams = [
          `taxonomy=${taxonomy}`,
          `category=${5}`,
          `start_date=${start_date}`,
          `end_date=${end_date}`,
        ];
        return {
          url: `/innovo_scope2_energy?${queryParams.join("&")}`,
          method: "GET",
        };
      },
      providesTags: ["analytics"],
    }),
    ingestFileForCleanup: builder.mutation({
      query: (body) => {
        return {
          url: "process-document/",
          method: "POST",
          body,
        };
      },
    }),
    getFileFormats: builder.query({
      query: () => {
        return {
          url: "get-formats/",
        };
      },
    }),
    getAlgoEmissions: builder.query({
      query: (params) => {
        return {
          url: `/get_emission_logs`,
          params,
        };
      },
      invalidatesTags: ["input-items"],
    }),
    getEmissions: builder.query({
      query: (params) => {
        return {
          url: `/dashboard_sobha`,
          params,
        };
      },
      transformResponse: (response) => response.data,
    }),
    getWasteGeneration: builder.query({
      query: (params) => {
        return {
          url: `/dashboard_sobha_waste_generated`,
          params,
        };
      },
    }),
    getTemplates: builder.query({
      query: () => {
        return {
          url: `/get-formats/`,
        };
      },
    }),
    getUnMatchedNodesSuggestions: builder.mutation({
      query: (body) => {
        return {
          url: `/best_match_node`,
          method: "POST",
          body,
        };
      },
    }),
    getEmissionsOverview: builder.query({
      query: () => {
        return {
          url: `/ccts/fetch_emissions`,
          params: {
            start_date: `2024-01`,
            end_date: `2025-12`,
          },
        };
      },
    }),
    addOrganizationDetails: builder.mutation({
      query: (body) => {
        return {
          url: `ccts/add_org_details`,
          method: "POST",
          body,
        };
      },
    }),

    /* ------------- Maker-Checker --------------- */
    getActivitiesForGHGCategory: builder.query({
      query: (categoryId) => {
        return {
          url: `maker-checker/get_activities`,
          params: {
            category_id: categoryId,
          },
        };
      },
      providesTags: ["activities"],
    }),
    getActivitiesOnSearch: builder.mutation({
      query: (body) => {
        return {
          url: `/activity-explorer`,
          method: "POST",
          body,
        };
      },
    }),

    getUnits: builder.mutation({
      query: (body) => {
        return {
          url: `/activity-explorer/unit`,
          method: "POST",
          body,
        };
      },
    }),
    getSources: builder.mutation({
      query: (body) => {
        return {
          url: `/activity-explorer/source`,
          method: "POST",
          body,
        };
      },
    }),
    getRegions: builder.mutation({
      query: (body) => {
        return {
          url: `/activity-explorer/region`,
          method: "POST",
          body,
        };
      },
    }),
    getCategories: builder.mutation({
      query: (body) => {
        return {
          url: `/activity-explorer/category`,
          method: "POST",
          body,
        };
      },
    }),
    getRaisedRequests: builder.query({
      query: (body) => {
        return {
          url: `maker-checker/fetch_poc_data`,
          params: body,
        };
      },
      providesTags: ["raise-request"],
    }),
    getSubmittersApprovers: builder.query({
      query: () => {
        return {
          url: `/maker-checker/submitters_approvers`,
        };
      },
    }),
    getPocMatrix: builder.query({
      query: () => {
        return {
          url: `/maker-checker/get_matrix`,
        };
      },
      providesTags: ["poc"],
    }),
    raiseRequest: builder.mutation({
      query: (body) => {
        return {
          url: `/maker-checker/raise_data_request`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["raise-request"],
    }),
    addPocMatrix: builder.mutation({
      query: (body) => {
        return {
          url: `/maker-checker/add_matrix`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["poc"],
    }),
    addActivitiesToGHGCategories: builder.mutation({
      query: (body) => {
        return {
          url: `/maker-checker/add_activities`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["activities"],
    }),
    deleteActivities: builder.mutation({
      query: (body) => {
        return {
          url: `/maker-checker/delete_activities`,
          method: "DELETE",
          body,
        };
      },
      invalidatesTags: ["activities"],
    }),
    getRequests: builder.query({
      query: () => {
        return {
          url: `/maker-checker/fetch_request`,
        };
      },
      providesTags: ["admin", "submitter"],
    }),
    getApproverData: builder.query({
      query: () => {
        return {
          url: `/maker-checker/fetch_approvals`,
        };
      },
      providesTags: ["approver"],
    }),
    submitData: builder.mutation({
      query: (body) => {
        return {
          url: `/maker-checker/submit_data_request`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["submitter"],
    }),
    approveRequests: builder.mutation({
      query: (body) => {
        return {
          url: `/maker-checker/approve_request`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["approver"],
    }),
    overrideDataAdmin: builder.mutation({
      query: (body) => {
        return {
          url: `/maker-checker/approve_request_admin`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["admin"],
    }),
    ingestFileViaTemplate: builder.mutation({
      query: (body) => {
        return {
          url: "/process-document/",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["summary"],
    }),

    createManualApproverEntry: builder.mutation({
      query: (body) => {
        return {
          url: `/manual-submissions/approver_config`,
          method: "POST",
          body,
        };
      },
    }),
    addManualApprover: builder.mutation({
      query: (body) => {
        return {
          url: "/manual-submissions/approver-config",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["manual-approvers"],
    }),
    getManualApproverData: builder.query({
      query: (params) => {
        return {
          url: `/manual-submissions/approver-config`,
          params,
        };
      },
      providesTags: ["manual-approvers"],
    }),
    updateManualApprover: builder.mutation({
      query: ({ body }) => {
        return {
          url: `/manual-submissions/approver-config`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ["manual-approvers"],
    }),
    deleteManualApprovers: builder.mutation({
      query: (body) => {
        return {
          url: "/manual-submissions/approver-config",
          method: "DELETE",
          body,
        };
      },
      invalidatesTags: ["manual-approvers"],
    }),
    getStats: builder.query({
      query: () => {
        return {
          url: `/manual-submissions/submitter/summary`,
        };
      },
      providesTags: ["summary"],
    }),
    getSubmissions: builder.query({
      query: ({ page, limit, search, submission_type, status, sort_order }) => {
        return {
          url: `/manual-submissions/submitter/dashboard`,
          params: {
            page,
            limit,
            search,
            submission_type,
            status,
            sort_order,
          },
        };
      },
      providesTags: ["submissions"],
    }),

    getAssignedNodes: builder.query({
      query: () => {
        return {
          url: `/manual-submissions/nodes`,
        };
      },
    }),
    getSubmitterAccess: builder.query({
      query: ({ node_id } = {}) => {
        return {
          url: `/manual-submissions/submitter/access`,
          params: node_id != null ? { node_id } : undefined,
        };
      },
    }),
    getApproversDashboard: builder.query({
      query: ({ page = 1, limit = 10, submission_type } = {}) => {
        return {
          url: `/manual-submissions/approver/dashboard`,
          params: {
            page,
            limit,
            ...(submission_type ? { submission_type } : {}),
          },
        };
      },
    }),
    saveAllManualEntries: builder.mutation({
      query: (body) => {
        return {
          url: "/manual-submissions/submit",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["submissions", "summary"],
    }),

    approveOrRejectManual: builder.mutation({
      query: (body) => {
        return {
          url: "/manual-submissions/approve",
          method: "POST",
          body,
        };
      },
    }),

    getManualDataForReview: builder.query({
      query: (params) => {
        return {
          url: `/manual-submissions/submission/${params.id}`,
        };
      },
    }),
    deleteManualSubmissions: builder.mutation({
      query: (params) => {
        return {
          url: `/manual-submissions/submission`,
          method: "DELETE",
          params,
        };
      },
      invalidatesTags: ["submissions", "summary"],
    }),
    getProofDownload: builder.query({
      query: (path) => {
        return {
          url: `/manual-submissions/proof-download`,
          params: { path },
        };
      },
    }),
    getInformationFile: builder.query({
      query: (file_id) => ({
        url: `/information_file`,
        params: { file_id },
      }),
    }),
    getGhgHome: builder.query({
      query: ({ startDate, endDate, includeFLAG, compareStartDate, compareEndDate, nodeIds }) => ({
        url: "/ghgdashboard/home",
        params: {
          start_date: startDate,
          end_date: endDate,
          flag: includeFLAG,
          ...(nodeIds != null ? { node_ids: nodeIds } : {}),
          ...(compareStartDate ? { compare_start_date: compareStartDate } : {}),
          ...(compareEndDate ? { compare_end_date: compareEndDate } : {}),
        },
      }),
      providesTags: ["GhgHome"],
    }),

    getNodeEmissions: builder.query({
      query: ({
        nodeIds,
        nodeName,
        startDate,
        endDate,
        includeFLAG,
        compareStartDate,
        compareEndDate,
      }) => ({
        url: "/ghgdashboard/node-emissions",
        params: {
          start_date: startDate,
          end_date: endDate,
          node_ids: nodeIds,
          flag: includeFLAG,
          ...(compareStartDate ? { compare_start_date: compareStartDate } : {}),
          ...(compareEndDate ? { compare_end_date: compareEndDate } : {}),
        },
      }),
      providesTags: (_r, _e, arg) => [
        { type: "NodeEmissions", id: arg.nodeIds ?? "root" },
      ],
    }),

    getFlagBreakdown: builder.query({
      query: ({ startDate, endDate, compareStartDate, compareEndDate, nodeIds }) => ({
        url: "/ghgdashboard/flag-breakdown",
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(nodeIds != null ? { node_ids: nodeIds } : {}),
          ...(compareStartDate ? { compare_start_date: compareStartDate } : {}),
          ...(compareEndDate ? { compare_end_date: compareEndDate } : {}),
        },
      }),
      providesTags: ["FlagBreakdown"],
    }),

    getFlagNodeBreakdown: builder.query({
      query: ({ nodeIds, startDate, endDate, compareStartDate, compareEndDate } = {}) => ({
        url: "/ghgdashboard/flag-node-breakdown",
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(nodeIds != null ? { node_ids: nodeIds } : {}),
          ...(compareStartDate ? { compare_start_date: compareStartDate } : {}),
          ...(compareEndDate ? { compare_end_date: compareEndDate } : {}),
        },
      }),
      providesTags: (_r, _e, arg) => [
        { type: "FlagNodeBreakdown", id: arg?.nodeIds ?? "root" },
      ],
    }),

    getActivityBreakdown: builder.query({
      query: ({
        categoryId,
        search = "",
        offset = 0,
        limit = 100,
        startDate,
        endDate,
        includeFLAG,
        nodeIds,
      }) => ({
        url: "/ghgdashboard/activity-breakdown",
        params: {
          category_id: categoryId,
          search,
          offset,
          ...(startDate ? { start_date: startDate } : {}),
          ...(limit ? { limit } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          flag: includeFLAG,
          ...(nodeIds != null ? { node_ids: nodeIds } : {}),
        },
      }),
      providesTags: (_r, _e, arg) => [
        { type: "ActivityBreakdown", id: arg.categoryId },
      ],
    }),
    getGroups: builder.query({
      query: ({ categoryId, startDate, endDate, includeFLAG, nodeIds } = {}) => ({
        url: "/ghgdashboard/groups",
        params: {
          ...(categoryId ? { category_id: categoryId } : {}),
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          flag: includeFLAG,
          ...(nodeIds != null ? { node_ids: nodeIds } : {}),
        },
      }),
      providesTags: ["Groups"],
    }),

    createGroup: builder.mutation({
      query: (body) => ({ url: "/ghgdashboard/groups", method: "POST", body }),
      invalidatesTags: ["Groups", "Views"],
    }),

    addActivityToGroup: builder.mutation({
      query: (body) => ({
        url: "/ghgdashboard/groups/activities",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Groups", "Views"],
    }),

    getViews: builder.query({
      query: ({ startDate, endDate, categoryId, includeFLAG, nodeIds } = {}) => ({
        url: "/ghgdashboard/views",
        params: {
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          ...(categoryId ? { category_id: categoryId } : {}),
          flag: includeFLAG,
          ...(nodeIds != null ? { node_ids: nodeIds } : {}),
        },
      }),
      providesTags: ["Views"],
    }),

    createView: builder.mutation({
      query: (body) => ({ url: "/ghgdashboard/views", method: "POST", body }),
      invalidatesTags: ["Views"],
    }),

    addGroupToView: builder.mutation({
      query: (body) => ({
        url: "/ghgdashboard/views/groups",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Views"],
    }),

    removeActivityFromGroup: builder.mutation({
      query: (body) => ({
        url: "/ghgdashboard/groups/activities",
        method: "PUT",
        body: { ...body, action: "remove" },
      }),
      invalidatesTags: ["Groups", "Views"],
    }),

    removeGroupFromView: builder.mutation({
      query: (body) => ({
        url: "/ghgdashboard/views/groups",
        method: "PUT",
        body: { ...body, action: "remove" },
      }),
      invalidatesTags: ["Views"],
    }),

    deleteGroup: builder.mutation({
      query: (groupId) => ({
        url: `/ghgdashboard/groups/${groupId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Groups", "Views"],
    }),

    deleteView: builder.mutation({
      query: (viewId) => ({
        url: `/ghgdashboard/views/${viewId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Views"],
    }),
    getReportTemplates: builder.query({
      query: ({ name, format } = {}) => ({
        url: `/ghg/protocol/templates`,
        params: {
          ...(name ? { name } : {}),
          ...(format && format !== "all" ? { format } : {}),
        },
      }),
      providesTags: ["template"],
    }),
    getTemplatePreview: builder.query({
      query: ({ templateId, fy_year, nodeIds }) => ({
        url: `/ghg/protocol/templates`,
        params: {
          template_id: templateId,
          ...(fy_year ? { fy_year: fy_year } : {}),
          ...(nodeIds ? { node_ids: nodeIds } : {}),
        },
      }),
    }),
    generateReport: builder.mutation({
      query: (payload) => ({
        url: `/ghg/protocol/reports`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["history"],
    }),
    reportsHistory: builder.query({
      query: ({ page = 1, limit = 8, search } = {}) => ({
        url: `/ghg/protocol/reports`,
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ["history"],
    }),
    downloadReport: builder.query({
      query: ({ reportId }) => ({
        url: `/ghg/protocol/reports/${reportId}/download`,
        responseHandler: (response) => response.blob(),
      }),
    }),
    deleteReport: builder.mutation({
      query: ({ reportId }) => ({
        url: `/ghg/protocol/reports/${reportId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["history"],
    }),
    uploadReportTemplate: builder.mutation({
      query: ({ file, name, description }) => {
        const formData = new FormData();

        formData.append("file", file);
        formData.append("name", name);
        formData.append("description", description || "");
        return {
          url: `/ghg/protocol/templates`,
          method: "POST",
          body: formData,
        };
      },
    }),
    formatFilesWithAI: builder.mutation({
      query: (files) => ({
        url: `/ai-process-files/`,
        body: files,
        method: "POST",
      }),
    }),
    getFileStatus: builder.query({
      query: (params) => ({
        url: `/ai-process-files/status/`,
        params: params ?? undefined,
      }),
    }),
    extractRelevantKeys: builder.mutation({
      query: (body) => ({
        url: `/ai-process-files/template-key-mapping/`,
        body,
        method: "POST",
      }),
    }),
    setTemplate: builder.mutation({
      query: (body) => ({
        url: `/ai-process-files/set-template/`,
        body,
        method: "POST",
      }),
    }),
    submitBatch: builder.mutation({
      query: (body) => ({
        url: `/ai-process-files/submit-batch/`,
        body,
        method: "POST",
      }),
    }),
    saveEntries: builder.mutation({
      query: (body) => ({
        url: `/ai-process-files/save-entries/`,
        body,
        method: "POST",
      }),
    }),
    editBatch: builder.mutation({
      query: (body) => ({
        url: `/ai-process-files/batch/`,
        body,
        method: "PATCH",
      }),
    }),
    deleteBatch: builder.mutation({
      query: (batch_id) => ({
        url: `/ai-process-files/batch/`,
        body: { batch_id },
        method: "DELETE",
      }),
      invalidatesTags: ["summary"],
    }),
    updateBatchRow: builder.mutation({
      query: (body) => ({
        url: `/ai-process-files/batch-row/`,
        body,
        method: "PATCH",
      }),
    }),
    getActivitiesByCategory: builder.query({
      query: (category_id) => ({
        url: `/maker-checker/get_activities`,
        params: { category_id },
      }),
    }),
    getBatchDetails: builder.query({
      query: (batch_id) => ({
        url: `/ai-process-files/batch-details/`,
        params: { batch_id },
      }),
    }),
    getAiTemplates: builder.query({
      query: () => ({
        url: `/ai-process-files/templates/`,
      }),
    }),
    deleteAiTemplate: builder.mutation({
      query: (template_id) => ({
        url: `/ai-process-files/templates/`,
        method: "DELETE",
        params: { template_id },
      }),
    }),
  s3ToBlob: builder.query({
    query: (s3_path) => ({
      url: `/ai-process-files/blob-from-s3/`,
      params: { s3_path },
    }),
    keepUnusedDataFor: 0,
  }),

    configCreation: builder.query({
      query: () => ({
        url: "/ghg/categories",
      }),
    }),

    getCategoryConfig: builder.query({
      query: ({ org_id, category_id }) => ({
        url: "/ghg/category-config",
        params: { org_id, category_id },
      }),
      providesTags: (_r, _e, arg) => [
        { type: "CategoryConfig", id: arg.category_id },
      ],
    }),
    getCountries: builder.query({
      query: ({ type }) => ({
        url: "/setup/config-options",
        params: { type },
      }),
    }),

    getTenantConfig: builder.query({
      query: () => ({
        url: "/setup/tenant-config",
      }),
      providesTags: ['tenant-config']
    }),
    getHierarchy: builder.query({
      query: ({nodeId} = {}) => ({
        url: "/setup/org-hierarchy",
        params: {
          ...(nodeId && {node_id: nodeId})
        }
      }),
      providesTags: ['org', 'org-details']
    }),
    addOrgDetails: builder.mutation({
      query: (body) => ({
        url: "/setup/org-hierarchy/node",
        method: "POST",
        body,
      }),
      invalidatesTags: ['org']
    }),
    editOrgDetails: builder.mutation({
      query: ({ payload, selectedNodeId }) => ({
        url: `/setup/org-hierarchy/node/${selectedNodeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ['org-details']
    }),
    getConfigOptions: builder.query({
      query: (params) => ({
        url: "/setup/config-options",
        params,
      }),
    }),
    setTenantConfig: builder.mutation({
      query: (body) => ({
        url: `/setup/tenant-config`,
        body,
        method: "POST"
      }),
      invalidatesTags: ['tenant-config']
    }),
    
  }),
});

export const {
  useGetWasteGenerationQuery,
  useGetEmissionsQuery,
  useGetTokenMutation,
  useGetActivitiesQuery,
  useGetMapActivitiesQuery,
  useLazyGetMapActivitiesQuery,
  useManualMatchMutation,
  useGetCategoryActivitiesQuery,
  useSearchAllActivitiesQuery,
  useLazySearchAllActivitiesQuery,
  useLazyGetInnovoGRIReportExcelQuery,
  useLazyGetInnovoISOReportQuery,
  useGetInnovoScope1FugitiveQuery,
  useGetInnovoScope2EnergyQuery,
  useIngestFileForCleanupMutation,
  useGetFileFormatsQuery,
  useGetInnovoScope3WasteQuery,
  useGetAlgoEmissionsQuery,
  useGetUnMatchedNodesSuggestionsMutation,
  useGetEmissionsOverviewQuery,
  useAddOrganizationDetailsMutation,
  useAddActivitiesToGHGCategoriesMutation,
  useGetActivitiesForGHGCategoryQuery,
  useGetCategoriesMutation,
  useGetRegionsMutation,
  useGetSourcesMutation,
  useGetUnitsMutation,
  useGetActivitiesOnSearchMutation,
  useDeleteActivitiesMutation,
  useGetSubmittersApproversQuery,
  useGetPocMatrixQuery,
  useAddPocMatrixMutation,
  useGetRaisedRequestsQuery,
  useRaiseRequestMutation,
  useGetRequestsQuery,
  useSubmitDataMutation,
  useGetApproverDataQuery,
  useApproveRequestsMutation,
  useOverrideDataAdminMutation,
  useGetTemplatesQuery,
  useIngestFileViaTemplateMutation,
  useCreateManualApproverEntryMutation,
  useGetManualApproverDataQuery,
  useUpdateManualApproverMutation,
  useAddManualApproverMutation,
  useDeleteManualApproversMutation,

  useGetGhgHomeQuery,
  useGetNodeEmissionsQuery,
  useGetFlagBreakdownQuery,
  useGetFlagNodeBreakdownQuery,
  useGetActivityBreakdownQuery,
  useGetGroupsQuery,
  useCreateGroupMutation,
  useAddActivityToGroupMutation,
  useGetViewsQuery,
  useCreateViewMutation,
  useAddGroupToViewMutation,
  useRemoveActivityFromGroupMutation,
  useRemoveGroupFromViewMutation,
  useDeleteGroupMutation,
  useDeleteViewMutation,

  useGetStatsQuery,
  useGetSubmissionsQuery,
  useGetAssignedNodesQuery,
  useGetSubmitterAccessQuery,
  useGetProofDownloadQuery,
  useLazyGetProofDownloadQuery,
  useGetApproversDashboardQuery,
  useSaveAllManualEntriesMutation,
  useApproveOrRejectManualMutation,
  useGetManualDataForReviewQuery,
  useLazyGetInformationFileQuery,
  useDeleteManualSubmissionsMutation,
  useGetReportTemplatesQuery,
  useGetTemplatePreviewQuery,
  useGenerateReportMutation,
  useReportsHistoryQuery,
  useLazyDownloadReportQuery,
  useDeleteReportMutation,
  useUploadReportTemplateMutation,
  useGetFileStatusQuery,
  useLazyGetFileStatusQuery,
  useFormatFilesWithAIMutation,
  useExtractRelevantKeysMutation,
  useSetTemplateMutation,
  useSubmitBatchMutation,
  useSaveEntriesMutation,
  useEditBatchMutation,
  useDeleteBatchMutation,
  useUpdateBatchRowMutation,
  useLazyGetActivitiesByCategoryQuery,
  useS3ToBlobQuery,
  useLazyGetActivityBreakdownQuery,
  useLazyS3ToBlobQuery,
  useGetBatchDetailsQuery,
  useLazyGetBatchDetailsQuery,
  useGetAiTemplatesQuery,
  useDeleteAiTemplateMutation,
  useConfigCreationQuery,
  useGetCategoryConfigQuery,
  useLazyGetCategoryConfigQuery,
  useGetCountriesQuery,
  useGetTenantConfigQuery,
  useGetHierarchyQuery,
  useLazyGetHierarchyQuery,
  useAddOrgDetailsMutation,
  useEditOrgDetailsMutation,
  useGetConfigOptionsQuery,
  useSetTenantConfigMutation,
  useAddNodeToHierarchyMutation,
  useUpdateNodeInHierarchyMutation,
  useDeleteNodeInHierarchyMutation,
} = activitiesApi;
